/**
 * StudentReimbursementsView.jsx — FM4C Student Reimbursement View
 *
 * Embedded in MyFinance.jsx as the "Reimbursements" tab.
 * INV-FM4C-STUDENT-01: No RBAC codes, no internal state machine codes displayed.
 * INV-FM4C-STUDENT-02: "Pending Government Funding" is never shown as overdue.
 *
 * BOUND-REOPEN-FM4C-03 scope: New component only — no changes to existing tabs.
 */

import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, FileText, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, Globe } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("academiaos_token") || localStorage.getItem("token");
}

const STATUS_ICONS = {
  "Eligible": <CheckCircle className="w-4 h-4 text-emerald-500" />,
  "Under Review": <Clock className="w-4 h-4 text-amber-500" />,
  "Not Eligible": <XCircle className="w-4 h-4 text-red-500" />,
  "Conditionally Eligible": <AlertCircle className="w-4 h-4 text-orange-400" />,
  "Claim Filed with Authority": <FileText className="w-4 h-4 text-blue-500" />,
  "Acknowledged by Authority": <CheckCircle className="w-4 h-4 text-blue-600" />,
  "Approved by Authority": <CheckCircle className="w-4 h-4 text-emerald-600" />,
  "Partially Approved by Authority": <CheckCircle className="w-4 h-4 text-emerald-400" />,
  "Not Approved by Authority": <XCircle className="w-4 h-4 text-red-500" />,
  "Approval Withdrawn by Authority": <AlertCircle className="w-4 h-4 text-orange-500" />,
  "Full Payment Received from Authority": <CheckCircle className="w-4 h-4 text-emerald-600" />,
};

function AxisBadge({ label, value, variant = "neutral" }) {
  const colors = {
    positive: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border border-amber-200",
    negative: "bg-red-50 text-red-700 border border-red-200",
    neutral: "bg-slate-50 text-slate-600 border border-slate-200",
    info: "bg-blue-50 text-blue-700 border border-blue-200",
  };
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-slate-400 font-medium uppercase tracking-wide">{label}</span>
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${colors[variant]}`}>
        {STATUS_ICONS[value]}
        {value}
      </span>
    </div>
  );
}

function getVariant(value, axis) {
  const positive = ["Eligible", "Claim Filed with Authority", "Acknowledged by Authority",
    "Ready to File", "Approved by Authority", "Partially Approved by Authority",
    "Full Payment Received from Authority", "Reconciled", "Closed"];
  const negative = ["Not Eligible", "Not Approved by Authority", "Approval Withdrawn by Authority",
    "Claim Withdrawn"];
  const pending = ["Under Review", "Documents Required", "Conditionally Eligible",
    "Awaiting Authority Decision", "Review in Progress", "Payment Pending from Authority",
    "Partial Payment Received from Authority"];
  if (positive.includes(value)) return "positive";
  if (negative.includes(value)) return "negative";
  if (pending.includes(value)) return "pending";
  return "neutral";
}

function AmountRow({ label, value, highlight = false, note = null }) {
  const fmt = (v) => {
    const n = parseFloat(v || 0);
    return isNaN(n) ? "₹0.00" : `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  };
  return (
    <div className={`flex items-start justify-between py-2 ${highlight ? "font-semibold" : ""}`}>
      <div>
        <span className={`text-sm ${highlight ? "text-slate-800" : "text-slate-600"}`}>{label}</span>
        {note && <p className="text-xs text-slate-400 mt-0.5">{note}</p>}
      </div>
      <span className={`text-sm tabular-nums ${highlight ? "text-slate-900" : "text-slate-700"}`}>
        {fmt(value)}
      </span>
    </div>
  );
}

function DocumentChecklist({ docs }) {
  if (!docs || docs.length === 0) {
    return <p className="text-sm text-slate-400 italic">No documents submitted yet.</p>;
  }
  return (
    <div className="space-y-2">
      {docs.map((d) => (
        <div key={d.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50 last:border-0">
          <span className="text-slate-700">{d.document_type}</span>
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${
              d.verification_state === "VERIFIED"
                ? "bg-emerald-50 text-emerald-700"
                : d.verification_state === "REJECTED"
                ? "bg-red-50 text-red-700"
                : "bg-amber-50 text-amber-700"
            }`}
          >
            {d.verification_state === "VERIFIED" ? "Verified" : d.verification_state === "REJECTED" ? "Rejected" : "Pending"}
          </span>
        </div>
      ))}
    </div>
  );
}

function ReimbursementCard({ record, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  // FM4D BOUND-REOPEN-FM4D-03: India Compliance supplementary state
  const [indiaData, setIndiaData] = useState(null);
  const [loadingIndia, setLoadingIndia] = useState(false);

  const loadDetail = async () => {
    if (!expanded) {
      setLoading(true);
      setLoadingIndia(true);
      try {
        // FM4C detail (existing — unchanged)
        const res = await fetch(`${API}/api/student/me/finance/fm4c/reimbursements/${record.id}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDetail(data.reimbursement);
        }
      } catch (e) {
        toast.error("Failed to load details");
      } finally {
        setLoading(false);
      }
      // FM4D: load India compliance (supplementary — 404 is acceptable, not an error)
      try {
        const indiaRes = await fetch(`${API}/api/student/me/finance/fm4d/compliance/${record.id}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (indiaRes.ok) {
          const indiaJson = await indiaRes.json();
          setIndiaData(indiaJson.india_compliance || null);
        } else {
          setIndiaData(null); // No India compliance record — normal, not an error
        }
      } catch {
        setIndiaData(null);
      } finally {
        setLoadingIndia(false);
      }
    }
    setExpanded(!expanded);
  };

  const sanctionStatus = record.sanction;
  const showReversal = ["Approval Withdrawn by Authority"].includes(sanctionStatus);
  const hasPendingFunding = parseFloat(record.pending_government_funding || 0) > 0;
  const hasGovtReceivable = parseFloat(record.government_receivable || 0) > 0;

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      data-testid={`reimbursement-card-${record.id}`}
    >
      {/* Header */}
      <div className="px-5 py-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">{record.scheme_name}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{record.sponsoring_authority} · {record.academic_period}</p>
          </div>
          <div className="flex items-center gap-3">
            {record.is_closed && (
              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded border border-slate-200">
                Closed
              </span>
            )}
            <button
              onClick={loadDetail}
              data-testid={`expand-record-${record.id}`}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* 4-axis status badges */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <AxisBadge label="Eligibility" value={record.eligibility} variant={getVariant(record.eligibility)} />
          <AxisBadge label="Claim" value={record.claim} variant={getVariant(record.claim)} />
          <AxisBadge label="Sanction" value={record.sanction} variant={getVariant(record.sanction)} />
          <AxisBadge label="Receipt" value={record.receipt} variant={getVariant(record.receipt)} />
        </div>

        {/* Financial summary */}
        <div className="mt-4 pt-3 border-t border-slate-100 space-y-0.5 divide-y divide-slate-50">
          <AmountRow label="Eligible Amount" value={record.eligible_amount} />
          {hasPendingFunding && (
            /* INV-FM4C-STUDENT-02: Pending Govt Funding is never shown as overdue */
            <AmountRow
              label="Pending Government Funding"
              value={record.pending_government_funding}
              note="This amount is awaiting a formal decision from the authority. It does not affect your fee balance yet."
            />
          )}
          {hasGovtReceivable && (
            <AmountRow
              label="Sanctioned by Authority"
              value={record.government_receivable}
              highlight
              note="The institution is awaiting this amount from the government. This is not your liability."
            />
          )}
          {hasGovtReceivable && (
            <AmountRow
              label="Received by Institution"
              value={record.institution_received}
            />
          )}
          {hasGovtReceivable && (
            <AmountRow
              label="Outstanding from Authority"
              value={record.government_receivable_outstanding}
            />
          )}
        </div>

        {/* Reversal notice — INV-FM4-14 */}
        {showReversal && (
          <div className="mt-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
            <p className="text-xs font-medium text-orange-700">
              The authority's approval was withdrawn. Please check the details below and contact the finance office for guidance.
            </p>
          </div>
        )}
      </div>

      {/* Expandable detail */}
      {expanded && (
        <div className="px-5 pb-5 pt-0 border-t border-slate-100">
          {loading ? (
            <div className="flex justify-center py-6">
              <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : detail ? (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 mt-4">
                Document Checklist
              </h4>
              <DocumentChecklist docs={detail.documents} />
            </div>
          ) : null}

          {/* FM4D BOUND-REOPEN-FM4D-03: Government Scheme Status panel
              INV-FM4D-STUDENT-01: Shows only approved non-sensitive fields.
              INV-FM4C-STUDENT-02: Government Receivable is never shown as student liability. */}
          {!loadingIndia && indiaData && (
            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="govt-scheme-status-panel">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-4 h-4 text-indigo-500" />
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Government Scheme Status
                </h4>
              </div>
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-3">
                {/* Scheme context */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-400">Scheme</span>
                    <p className="font-medium text-slate-700">{indiaData.scheme_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Authority</span>
                    <p className="font-medium text-slate-700">{indiaData.sponsoring_authority}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Academic Period</span>
                    <p className="font-medium text-slate-700">{indiaData.academic_period}</p>
                  </div>
                  {indiaData.application_number && (
                    <div>
                      <span className="text-slate-400">Application No.</span>
                      <p className="font-medium text-slate-700">{indiaData.application_number}</p>
                    </div>
                  )}
                </div>

                {/* Government funding status — NOT shown as student liability */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {parseFloat(indiaData.sanctioned_amount) > 0 && (
                    <div className="col-span-2 bg-white rounded-lg p-2 border border-indigo-100">
                      <span className="text-slate-400">Sanctioned by Authority</span>
                      <p className="font-semibold text-indigo-700">
                        ₹{parseFloat(indiaData.sanctioned_amount).toLocaleString("en-IN")}
                      </p>
                      <p className="text-slate-400 text-xs mt-0.5">
                        The institution is awaiting this amount from the government. This is not your liability.
                      </p>
                    </div>
                  )}
                  {parseFloat(indiaData.pending_government_funding) > 0 && (
                    <div>
                      <span className="text-slate-400">Pending Govt. Funding</span>
                      <p className="font-medium text-slate-700">₹{parseFloat(indiaData.pending_government_funding).toLocaleString("en-IN")}</p>
                    </div>
                  )}
                  {parseFloat(indiaData.amount_received_by_institution) > 0 && (
                    <div>
                      <span className="text-slate-400">Received by Institution</span>
                      <p className="font-medium text-slate-700">₹{parseFloat(indiaData.amount_received_by_institution).toLocaleString("en-IN")}</p>
                    </div>
                  )}
                </div>

                {/* Operational status (portal plain text) */}
                {indiaData.government_scheme_status && indiaData.government_scheme_status !== "No status update available" && (
                  <div className="text-xs">
                    <span className="text-slate-400">Government Scheme Status</span>
                    <p className="font-medium text-indigo-700">{indiaData.government_scheme_status}</p>
                  </div>
                )}

                {/* Document status indicators */}
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {[
                    { label: "Income Certificate", val: indiaData.income_certificate_submitted },
                    { label: "Caste Certificate", val: indiaData.caste_certificate_submitted },
                    { label: "Domicile Certificate", val: indiaData.domicile_certificate_submitted },
                  ].filter(d => d.val !== null && d.val !== undefined).map(d => (
                    <div key={d.label} className="flex items-center gap-1.5">
                      {d.val
                        ? <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        : <Clock className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                      <span className={d.val ? "text-slate-600" : "text-amber-700"}>
                        {d.label}: {d.val ? "Submitted" : "Pending"}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Boolean milestone indicators */}
                <div className="flex flex-wrap gap-2 text-xs">
                  {indiaData.authority_acknowledgement_received && (
                    <span className="flex items-center gap-1 text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
                      <CheckCircle className="w-3.5 h-3.5" /> Authority Acknowledged
                    </span>
                  )}
                  {indiaData.sanction_order_received && (
                    <span className="flex items-center gap-1 text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">
                      <CheckCircle className="w-3.5 h-3.5" /> Sanction Order Received
                    </span>
                  )}
                  {(indiaData.treasury_payment_reference_available || indiaData.dbt_payment_reference_available) && (
                    <span className="flex items-center gap-1 text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">
                      <CheckCircle className="w-3.5 h-3.5" /> Payment Reference Recorded
                    </span>
                  )}
                </div>

                {/* Bank validation */}
                {indiaData.bank_validation_status && indiaData.bank_validation_status !== "Not Required" && (
                  <div className="text-xs">
                    <span className="text-slate-400">Bank Details</span>
                    <p className="font-medium text-slate-700">{indiaData.bank_validation_status}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function StudentReimbursementsView() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/student/me/finance/fm4c/reimbursements`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Failed to load reimbursements");
      const data = await res.json();
      setRecords(data.reimbursements || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12" data-testid="reimbursements-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-300" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-slate-400" data-testid="reimbursements-error">
        <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="text-center py-16" data-testid="reimbursements-empty">
        <FileText className="w-10 h-10 mx-auto mb-3 text-slate-200" />
        <p className="text-sm font-medium text-slate-400">No reimbursement records found</p>
        <p className="text-xs text-slate-300 mt-1">
          If you are eligible for a government reimbursement scheme, contact the finance office to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="student-reimbursements-view">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {records.length} reimbursement record{records.length !== 1 ? "s" : ""} found
        </p>
        <button
          onClick={loadRecords}
          className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400"
          data-testid="refresh-reimbursements-btn"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {records.map((r) => (
        <ReimbursementCard key={r.id} record={r} onRefresh={loadRecords} />
      ))}
    </div>
  );
}
